// DateRangePicker.tsx
import { Button, DatePicker, Popover, Radio } from 'antd'
import 'antd/dist/reset.css'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import cx from 'classnames'

type RelativeKey =
  // | 'today'
  // | 'yesterday'
  | 'last3days'
  | 'last7days'
  | 'last14days'
  | 'last30days'
  | 'lastWeek'
  | 'lastMonth'
  | 'last3months'
  | 'last6months'
  | 'lastYear'
  // | 'tomorrow'
  | 'next3days'
  | 'next7days'
  | 'next14days'
  | 'next30days'
  | 'nextWeek'
  | 'nextMonth'
  | 'next3months'
  | 'next6months'
  | 'nextYear'
  | 'thisWeek'
  | 'thisMonth'
  | 'last3businessDays'
  | 'last5businessDays'
  | 'last7businessDays'
  | 'next3businessDays'
  | 'next5businessDays'
  | 'next7businessDays'
  | 'custom'

export type DateRangeResult = {
  relativeDate?: RelativeKey
  startDate?: string
  endDate?: string
}

const CURRENT_OPTIONS: { label: string; value: RelativeKey }[] = [
  { label: 'This week', value: 'thisWeek' },
  { label: 'This month', value: 'thisMonth' },
]

const PAST_OPTIONS: { label: string; value: RelativeKey }[] = [
  { label: 'Last 3 business days', value: 'last3businessDays' },
  { label: 'Last 5 business days', value: 'last5businessDays' },
  { label: 'Last 7 business days', value: 'last7businessDays' },
  { label: 'Last 3 days', value: 'last3days' },
  { label: 'Last 7 days', value: 'last7days' },
  { label: 'Last 14 days', value: 'last14days' },
  { label: 'Last 30 days', value: 'last30days' },
  { label: 'Last week', value: 'lastWeek' },
  { label: 'Last month', value: 'lastMonth' },
  { label: 'Last 3 months', value: 'last3months' },
  { label: 'Last 6 months', value: 'last6months' },
  { label: 'Last year', value: 'lastYear' },
]

const FUTURE_OPTIONS: { label: string; value: RelativeKey }[] = [
  { label: 'Next 3 business days', value: 'next3businessDays' },
  { label: 'Next 5 business days', value: 'next5businessDays' },
  { label: 'Next 7 business days', value: 'next7businessDays' },
  { label: 'Next 3 days', value: 'next3days' },
  { label: 'Next 7 days', value: 'next7days' },
  { label: 'Next 14 days', value: 'next14days' },
  { label: 'Next 30 days', value: 'next30days' },
  { label: 'Next week', value: 'nextWeek' },
  { label: 'Next month', value: 'nextMonth' },
  { label: 'Next 3 months', value: 'next3months' },
  { label: 'Next 6 months', value: 'next6months' },
  { label: 'Next year', value: 'nextYear' },
]

const ALL_OPTIONS = [...CURRENT_OPTIONS, ...PAST_OPTIONS, ...FUTURE_OPTIONS]

interface Props {
  onChange: (res: DateRangeResult) => void
  value?: DateRangeResult
  disabled?: boolean
  disableFutureDate?: boolean
  className?: string
  disablePastDate?: boolean
}

const RelativeDateRangePicker: React.FC<Props> = ({
  onChange,
  value,
  disabled,
  disableFutureDate,
  className,
  disablePastDate,
}) => {
  const [visible, setVisible] = useState(false)
  const [relativeDate, setRelativeDate] = useState<RelativeKey>(
    value?.relativeDate || 'custom'
  )
  const [editValue, setEditValue] = useState<DateRangeResult | null>(value)
  const [displayValue, setDisplayValue] = useState<string | null>(null)

  useEffect(() => {
    setDisplayValue(getDisplayValue())
  }, [])

  useEffect(() => {
    if (
      value?.relativeDate == null &&
      value?.startDate == null &&
      value?.endDate == null
    ) {
      if (displayValue) {
        // Handle clear display value
        setDisplayValue(null)
      }
    }
  }, [value])

  const handleOnChange = (newValue: DateRangeResult) => {
    if (
      !newValue.startDate &&
      !newValue.endDate &&
      newValue.relativeDate === 'custom'
    ) {
      onChange({
        relativeDate: null,
        startDate: null,
        endDate: null,
      })
    } else {
      onChange(newValue)
    }
  }

  const handleApply = () => {
    handleOnChange(editValue)
    setVisible(false)
    setDisplayValue(getDisplayValue())
  }

  const handleCancel = () => {
    setRelativeDate(value?.relativeDate || 'custom')
    setVisible(false)
    setEditValue(value)
  }

  const getDisplayValue = () => {
    if (relativeDate === 'custom') {
      if (editValue?.startDate && editValue?.endDate) {
        return `${dayjs(editValue.startDate).format('DD MMM YYYY')} - ${dayjs(
          editValue.endDate
        ).format('DD MMM YYYY')}`
      } else {
        return ''
      }
    } else {
      return ALL_OPTIONS.find(opt => opt.value === relativeDate)?.label || ''
    }
  }

  const handleClearValue = () => {
    const newValue: DateRangeResult = {
      relativeDate: 'custom',
      endDate: null,
      startDate: null,
    }

    setEditValue(newValue)
    setRelativeDate('custom')
    setDisplayValue(null)
    handleOnChange(newValue)
  }

  const handlePopoverOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // cancel selected value when clicking outside the popover
      handleCancel()
    }
    setVisible(nextOpen)
  }

  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current > dayjs().endOf('day')
  }

  const disablePastDates = (current: dayjs.Dayjs) => {
    return current && current < dayjs().startOf('day')
  }

  const content = (
    <div className="w-96">
      <Radio.Group
        onChange={e => {
          setRelativeDate(e.target.value)
          setEditValue({
            relativeDate: e.target.value,
          })
        }}
        value={relativeDate}
        className="w-full"
      >
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Radio key={'thisWeek'} value={'thisWeek'}>
            This week
          </Radio>
          <Radio key={'thisMonth'} value={'thisMonth'}>
            This month
          </Radio>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-2">
          {disableFutureDate ? (
            <>
              <div className="flex flex-col space-y-2">
                {PAST_OPTIONS.slice(
                  0,
                  Math.ceil(PAST_OPTIONS.length / 2)
                ).map(opt =>
                  opt.value ? (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                      className="!flex items-center"
                    >
                      {opt.label}
                    </Radio>
                  ) : (
                    <div className="h-6"></div>
                  )
                )}
              </div>
              <div className="flex flex-col space-y-2">
                {PAST_OPTIONS.slice(
                  Math.ceil(PAST_OPTIONS.length / 2)
                ).map(opt =>
                  opt.value ? (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                      className="!flex items-center"
                    >
                      {opt.label}
                    </Radio>
                  ) : (
                    <div className="h-6"></div>
                    )
                )}
              </div>
            </>
          ) : disablePastDate ? (
            <>
              <div className="flex flex-col space-y-2">
                {FUTURE_OPTIONS.slice(
                  0,
                  Math.ceil(FUTURE_OPTIONS.length / 2)
                ).map(opt =>
                  opt.value ? (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                      className="!flex items-center"
                    >
                      {opt.label}
                    </Radio>
                  ) : (
                    <div className="h-6"></div>
                  )
                )}
              </div>
              <div className="flex flex-col space-y-2">
                {FUTURE_OPTIONS.slice(
                  Math.ceil(FUTURE_OPTIONS.length / 2)
                ).map(opt =>
                  opt.value ? (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                        className="!flex items-center"
                    >
                      {opt.label}
                    </Radio>
                  ) : (
                    <div className="h-6"></div>
                  )
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col space-y-2">
                {PAST_OPTIONS.map(opt =>
                  opt.value ? (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                      className="!flex items-center"
                    >
                      {opt.label}
                    </Radio>
                  ) : (
                    <div className="h-6"></div>
                  )
                )}
              </div>
              <div className="flex flex-col space-y-2">
                {FUTURE_OPTIONS.map(opt =>
                  opt.value ? (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                      className="!flex items-center"
                    >
                      {opt.label}
                    </Radio>
                  ) : (
                    <div className="h-6"></div>
                  )
                )}
              </div>
            </>
          )}
        </div>
        <div className="mb-4">
          <Radio value="custom" className="!flex items-center">
            Custom
          </Radio>
        </div>
      </Radio.Group>

      {relativeDate === 'custom' && (
        <DatePicker.RangePicker
          format="DD MMM YYYY"
          value={[
            editValue?.startDate ? dayjs(editValue.startDate) : null,
            editValue?.endDate ? dayjs(editValue.endDate) : null,
          ]}
          onChange={value => {
            if (!value) {
              setEditValue({
                relativeDate: 'custom',
                startDate: null,
                endDate: null,
              })
              return
            } else {
              setEditValue({
                startDate: value?.[0]?.format('YYYY-MM-DD'),
                endDate: value?.[1]?.format('YYYY-MM-DD'),
                relativeDate: 'custom',
              })
            }
          }}
          allowClear
          className="w-full mb-4"
          disabled={disabled}
          disabledDate={current => {
            if (disableFutureDate) {
              return disableFutureDates(current)
            }
            if (disablePastDate) {
              return disablePastDates(current)
            }
            return false
          }}
        />
      )}

      <div className="flex justify-center gap-2">
        <Button type="primary" size="small" onClick={handleApply}>
          OK
        </Button>
        <Button
          size="small"
          onClick={() => {
            handleCancel()
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  )

  return (
    <div className={cx('relative-date-range-picker', className)}>
      <Popover
        content={content}
        trigger="click"
        placement="bottomLeft"
        open={visible}
        onOpenChange={handlePopoverOpenChange}
      >
        <Button
          disabled={disabled}
          className={cx(
            'justify-start pl-3 w-full',
            className?.includes('rounded-full') && 'rounded-full'
          )}
          onClick={() => setVisible(true)}
        >
          <div>
            {displayValue || <span className="text-grey-300">Select date range</span>}
          </div>
        </Button>
      </Popover>
      {displayValue && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-grey-400 hover:text-blue-400"
          onClick={e => {
            if (disabled) {
              return
            }
            e.stopPropagation()
            handleClearValue()
          }}
          title="Clear"
        >
          <CloseOutlined className="w-3 h-3" />
        </span>
      )}
    </div>
  )
}

export default RelativeDateRangePicker